import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { showDialog } from "@widgets/CommonDialog";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import CommonSpinner from "@widgets/CommonSpinner";
import { SnGridRef, ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { parseGridPaste } from "@shared/utils/GridPasteUtils";
import { useGridPaste } from "@shared/hooks/useGridPaste";

/**
 * 비용집계 팝업 — 계정별 원장(cos_elemaml_if)을 가져와 확인·수정한 뒤 프로젝트 경비로 집계한다.
 *   Fetch     : 요소비용집계(COS004)와 같은 계정별 원장 API 조회 → 그리드 표시(저장 안 함)
 *               엑셀에서 복사한 행을 그리드에 붙여넣기(Ctrl+V)하면 기존 데이터 뒤에 추가된다.
 *   Save      : 그리드 행으로 해당 기간의 cos_elemaml_if 교체(임시저장)
 *   Aggregate : cos_elemaml_if → pms_pjt_elemamh(경비 fg_budget='3') 반영 후 팝업 닫기
 * 메타: 조회조건 sys_popupctrl / 그리드 sys_popupgridcolumn(grid1) / 버튼 sys_headerctrl(grid1, cd_param=위 id).
 * 계정명·프로젝트명은 작업자 편의용 표시 컬럼이라 저장하지 않는다.
 */
const CD_MENU = "SN_PMS_CLS_REG_002_POP_02";

interface popupProps {
  selectedData?: { cd_biz?: string; dt_from?: string; dt_to?: string };
  modalRef?: any;
}

/** 붙여넣은 금액("1,234,000")·빈 값을 숫자로. */
const toNum = (v: any): number => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const SN_PMS_CLS_REG_002_POP_02 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const gridRef = useRef<SnGridRef>(null);

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColumns] = useState<ColumnConfig[]>([]);
    const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // 사업장 / 평가기간 — 초기값은 메인 조회조건, 이후 팝업 조회조건 변경에 반응.
    const [cd_biz, setCdBiz] = useState<string>(selectedData?.cd_biz ?? "");
    const [dt_from, setDtFrom] = useState<string>(selectedData?.dt_from ?? "");
    const [dt_to, setDtTo] = useState<string>(selectedData?.dt_to ?? "");
    const ym_end = String(dt_to).slice(0, 6);
    const ymLabel = `${ym_end.slice(0, 4)}년 ${ym_end.slice(4, 6)}월`;

    const acctNames = useRef<Record<string, string>>({});
    const pjtNames = useRef<Record<string, string>>({});
    // 집계 완료 여부 — handleConfirm 직후 getSelectedValue 가 읽으므로 state 대신 ref.
    const aggregatedRef = useRef(false);
    // API 가져오기·붙여넣기로 그리드를 통째로 바꾼 뒤 아직 임시저장 안 함(그리드 변경추적에 안 잡힘).
    const unsavedRef = useRef(false);
    const pasteAreaRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getSelectedValue: () =>
        aggregatedRef.current ? { aggregated: true } : undefined,
    }));

    const grid = (): SnGridRef => {
      if (!gridRef.current) throw new Error("grid1 is not mounted yet");
      return gridRef.current;
    };

    const onValueChanged = (e: any) => {
      const d = e?.rowData ?? {};
      if ("cd_biz" in d) setCdBiz(d.cd_biz ?? "");
      if ("dt_from" in d) setDtFrom(d.dt_from ?? "");
      if ("dt_to" in d) setDtTo(d.dt_to ?? "");
    };

    // 표시용 계정명·프로젝트명. API 계정코드는 7자리(내부 5자리 + "00")로 올 수 있어 둘 다 찾는다.
    const withNames = (list: any[]) =>
      list.map((r) => {
        const acct = String(r.cd_acct ?? "");
        return {
          ...r,
          nm_acct:
            acctNames.current[acct] ??
            acctNames.current[acct.replace(/00$/, "")] ??
            r.nm_acct ??
            "",
          nm_pjt: pjtNames.current[String(r.cd_pjt ?? "")] ?? r.nm_pjt ?? "",
        };
      });

    // 팝업 메타(조회조건·그리드) + 그리드 헤더 버튼 + 이름 매핑.
    // getInitPopUp 은 sys_headerctrl 을 안 내려줘서 공통 메타 쿼리(sys_query)로 따로 읽는다.
    useEffect(() => {
      (async () => {
        try {
          const [rtn, header, accts, pjts]: any[] = await Promise.all([
            getInitPopUpData(CD_MENU, {}),
            fnGetDataSet({
              id: "com_CommonMapper_11",
              params: {
                cd_firm: userInfo.cd_firm,
                cd_menu: CD_MENU,
                cd_form: "grid1",
              },
            }),
            fnGetDataSet({
              id: "sn_pms_cls_reg_002_pop_02_01",
              params: { cd_firm: userInfo.cd_firm },
            }),
            fnGetDataSet({
              id: "sn_pms_cls_reg_002_pop_02_02",
              params: { cd_firm: userInfo.cd_firm },
            }),
          ]);
          acctNames.current = Object.fromEntries(
            (Array.isArray(accts?.[0]) ? accts[0] : []).map((a: any) => [
              a.cd_acct,
              a.nm_acct,
            ])
          );
          pjtNames.current = Object.fromEntries(
            (Array.isArray(pjts?.[0]) ? pjts[0] : []).map((p: any) => [
              p.cd_pjt,
              p.nm_pjt,
            ])
          );
          setFormControlData(rtn.formControlData);
          setFormData({ ...rtn.formData, ...selectedData });
          setColumns(rtn.grid?.grid1 ?? []);
          setHeaderInfo({
            controls: Array.isArray(header?.[0]) ? header[0] : [],
            data: {},
          });
        } catch (error: any) {
          showDialog("error", error.message);
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    // 임시저장된 데이터 조회. 없으면 붙여넣기 시작점으로 빈 행 하나를 둔다.
    const loadSaved = async () => {
      const saved: any = await fnGetDataSet({
        id: "com_CommonMapper_13",
        params: { cd_firm: userInfo.cd_firm, cd_biz, ym_end },
      });
      const list = Array.isArray(saved?.[0]) ? saved[0] : [];
      setRows(withNames(list));
      grid().resetUpdate();
      unsavedRef.current = false;
    };

    // 사업장/평가기간이 바뀌면 임시저장 데이터 재조회.
    useEffect(() => {
      if (!cd_biz || !ym_end || !columns.length) return;
      (async () => {
        setLoading(true);
        try {
          await loadSaved();
        } catch (error: any) {
          showDialog("error", error.message);
        } finally {
          setLoading(false);
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cd_biz, ym_end, columns]);

    const allRows = (): any[] => {
      const out: any[] = [];
      grid().forEachNode((node: any) => node?.data && out.push(node.data));
      return out;
    };

    /**
     * 엑셀 붙여넣기 — 그리드 영역 어디서 Ctrl+V 해도 같은 결과(커서 위치·행 추가와 무관).
     * 컬럼 순서·날짜/숫자 변환·머리글·앞쪽 숨긴 열은 그리드 메타 기준 공용 처리(GridPasteUtils).
     * 기존 데이터 뒤에 이어 붙이고 빈 행은 버린다.
     */
    useGridPaste(pasteAreaRef, (text) => {
      const { rows: pasted, errors, headerSkipped } = parseGridPaste(text, columns);
      if (!pasted.length) {
        // 머리글이 섞여 들어와 남는 행이 없을 때만 알린다.
        // 빈 클립보드·표가 아닌 텍스트는 조용히 넘긴다(알릴 것이 없다).
        if (headerSkipped) {
          showDialog(
            "alter",
            "머리글(컬럼명) 행은 빼고 데이터 행만 복사해 주세요."
          );
        }
        return;
      }
      grid().stopEditing();
      const kept = allRows().filter((r) => r.cd_acct || r.dt_isu);
      setRows([...kept, ...withNames(pasted)]);
      grid().resetUpdate();
      unsavedRef.current = true;
      if (errors.length) {
        const detail = errors
          .slice(0, 10)
          .map((e) => `${e.line}번째 행 ${e.nm_column}(${e.value})`)
          .join(", ");
        showDialog(
          "alter",
          `${pasted.length}건 붙여넣음. 형식을 읽지 못한 칸: ${detail}${errors.length > 10 ? " 외" : ""}`
        );
      }
    });

    const checkPeriod = (): boolean => {
      if (cd_biz && ym_end) return true;
      showDialog("alter", "사업장과 평가기간을 확인해 주세요.");
      return false;
    };

    const onFetch = async () => {
      if (!checkPeriod()) return;
      if (allRows().some((r) => r.cd_acct || r.dt_isu)) {
        const ok = await showDialog(
          "confirm",
          "그리드 내용을 API 데이터로 바꿉니다. 계속하시겠습니까?"
        );
        if (!ok) return;
      }
      setLoading(true);
      try {
        const res: any = await callApi("SN_PMS_CLS_REG_002_Exp_Fetch", {
          cd_firm: userInfo.cd_firm,
          cd_biz,
          dt_from,
          dt_to,
        });
        if (!res?.status) return;
        // callApi 는 빈 결과를 {} 로 돌려준다.
        const list = Array.isArray(res.data) ? res.data : [];
        setRows(withNames(list));
        grid().resetUpdate();
        // API 행은 아직 저장 전이다 — 임시저장 전에 비용집계하지 않도록 표시해 둔다.
        unsavedRef.current = list.length > 0;
        if (!list.length) showDialog("alter", "조회된 데이터가 없습니다.");
      } finally {
        setLoading(false);
      }
    };

    const onSave = async () => {
      if (!checkPeriod()) return;
      grid().stopEditing();
      const payload = allRows()
        .map((r) => ({
          dt_isu: r.dt_isu ?? "",
          dt_fill: r.dt_fill ?? "",
          cd_acct: String(r.cd_acct ?? "").trim(),
          am_dr: toNum(r.am_dr),
          am_cr: toNum(r.am_cr),
          cd_dept: r.cd_dept ?? "",
          cd_pjt: String(r.cd_pjt ?? "").trim(),
          dc_rmk: r.dc_rmk ?? "",
        }))
        .filter((r) => r.cd_acct || String(r.dt_isu).trim());
      setLoading(true);
      try {
        const res: any = await callApi("SN_PMS_CLS_REG_002_Exp_IfSave", {
          cd_firm: userInfo.cd_firm,
          cd_biz,
          ym_end,
          rows: payload,
        });
        if (res?.status) await loadSaved();
      } finally {
        setLoading(false);
      }
    };

    // 저장 안 된 변경: API로 가져온 뒤 미저장 / 그리드 수정·삭제 / 붙여넣기·추가한 행(빈 행 제외).
    const isDirty = (): boolean => {
      if (unsavedRef.current) return true;
      const ch: any = grid().getChange();
      const filled = (r: any) => r?.cd_acct || r?.dt_isu;
      return (
        (ch?.update?.length ?? 0) > 0 ||
        (ch?.delete?.length ?? 0) > 0 ||
        (ch?.insert ?? []).some(filled)
      );
    };

    const onAggregate = async () => {
      if (!checkPeriod()) return;
      grid().stopEditing();
      if (isDirty()) {
        showDialog("alter", "저장되지 않은 변경이 있습니다. 임시저장 후 집계해 주세요.");
        return;
      }
      const ok = await showDialog(
        "confirm",
        `${ymLabel} 경비를 임시저장된 데이터로 다시 집계합니다. 계속하시겠습니까?`
      );
      if (!ok) return;
      setLoading(true);
      try {
        const res: any = await callApi("SN_PMS_CLS_REG_002_Exp_Aggregate", {
          cd_firm: userInfo.cd_firm,
          cd_biz,
          ym_end,
        });
        if (!res?.status) return;
        aggregatedRef.current = true;
        modalRef?.current?.handleConfirm?.(true); // 메인화면으로 복귀
      } finally {
        setLoading(false);
      }
    };

    // sys_headerctrl 버튼 — cd_param 이 클릭 id.
    const onBtnEtcClick = (id: string) => {
      switch (id) {
        case "Fetch":
          return onFetch();
        case "Save":
          return onSave();
        case "Aggregate":
          return onAggregate();
      }
    };

    return (
      <div className="flex flex-col h-full overflow-hidden">
        <CommonSpinner isLoading={loading} />
        <div className="flex-shrink-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
            onValueChanged={onValueChanged}
          />
        </div>
        {/* tabIndex: 빈 그리드를 클릭해도 이 영역이 포커스를 받아 Ctrl+V 가 여기로 온다 */}
        <div ref={pasteAreaRef} tabIndex={-1} className="flex-1 min-h-0 mt-2 outline-none">
          <SnGrid
            ref={gridRef}
            columnConfig={columns}
            rowData={rows}
            headerInfo={headerInfo}
            etcProps={{ rowSelection: "multiple", isEditable: true, title: "계정별 원장" }}
            header={
              <span className="text-sm text-gray-600 mr-2">
                평가기간 {ymLabel} · 엑셀 행을 복사해 그리드에서 Ctrl+V 하면 뒤에 추가됩니다
              </span>
            }
            events={{
              onBtnAddClick: () => grid().addRow(),
              onBtnDeleteClick: () => grid().deleteSelectedRows(),
              onBtnEtcClick,
            }}
          />
        </div>
      </div>
    );
  }
);

export default SN_PMS_CLS_REG_002_POP_02;
